// LoginModal.jsx
import React, { useEffect, useState, useRef } from "react";
import { FiChevronLeft, FiMail, FiEye, FiEyeOff } from "react-icons/fi";
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import superAdminApi from "./utils/superAdminApi";
import ReCAPTCHA from "react-google-recaptcha";

import logoFull from "./assets/zeno.png";
import InfoModal from "./modals/InfoModals";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const LoginModal = () => {
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const [showPwd, setShowPwd] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const showInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      showInfo("warning", "Eksik Bilgi", "Lütfen e-posta ve şifre giriniz.");
      return;
    }

    setLoading(true);
    try {
      const captchaToken = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();

      const response = await superAdminApi.post("/login", {
        email,
        password,
        recaptchaToken: captchaToken,
      });

      // Token’ı localStorage’a kaydet
      if (response.data.accessToken) {
        localStorage.setItem("superadmin_token", response.data.accessToken);
        localStorage.setItem(
          "superadmin_user",
          JSON.stringify(response.data.user)
        );
      }

      // Giriş başarılı uyarısı
      showInfo("success", "Giriş Başarılı", "Yönlendiriliyorsunuz...");

      // Dashboard’a yönlendir
      navigate(`/${response.data.user.id}/companies`);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Giriş başarısız. Lütfen bilgilerinizi kontrol edin.";
      showInfo("error", "Giriş Başarısız", msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] grid place-items-center p-2 sm:p-4">
      {/* Arka plan */}
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white rounded-[28px] shadow-2xl w-full max-w-[1200px]
                   h-[92vh] md:h-[90vh] overflow-y-auto md:overflow-hidden"
      >
        <button
          onClick={() => (window.location.href = "https://zenolearn.io")}
          className="absolute right-3.5 top-3.5 z-50 inline-flex items-center justify-center
             h-12 w-12 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
          aria-label="Geri"
          title="Geri Git"
        >
          <FiChevronLeft className="text-4xl" />
        </button>

        <div className="grid h-full grid-cols-1 lg:grid-cols-2">
          {/* Sol panel - Form */}
          <div className="px-6 sm:px-10 py-8 md:py-0 flex flex-col items-center justify-center text-center">
            <img
              src={logoFull}
              alt="ZENOLearn"
              className="h-12 sm:h-16 w-auto object-contain mx-auto"
            />
            <h1 className="text-3xl sm:text-4xl font-bold mt-5 sm:mt-6">
              Team Login
            </h1>

            <form
              onSubmit={handleSubmit}
              className="mt-10 md:mt-12 space-y-5 w-full max-w-md"
              aria-busy={loading}
              aria-live="polite"
            >
              {/* Email */}
              <label className="block text-left">
                <span className="block text-sm font-medium text-gray-600 mb-1">
                  Email Adresi
                </span>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    autoComplete="username"
                    required
                    placeholder="Email adresinizi yazınız"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-300
                               focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white disabled:opacity-60"
                  />
                </div>
              </label>

              {/* Password */}
              <label className="block text-left">
                <span className="block text-sm font-medium text-gray-600 mb-1">
                  Şifre
                </span>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    required
                    placeholder="Şifrenizi yazınız"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="w-full pr-10 pl-3 py-3 rounded-xl border border-gray-300
                               focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPwd ? "Şifre gizle" : "Şifre göster"}
                    disabled={loading}
                  >
                    {showPwd ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </label>

              <div className="pt-3 md:pt-4" />

              {/* Giriş butonu + loading */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full h-12 rounded-full text-white font-medium flex items-center justify-center transition-all
                  ${
                    loading
                      ? "bg-emerald-400 cursor-not-allowed"
                      : "bg-emerald-500 hover:bg-emerald-600"
                  }`}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-white animate-bounce"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-white animate-bounce [animation-delay:0.15s]"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-white animate-bounce [animation-delay:0.3s]"></div>
                  </div>
                ) : (
                  "Giriş yap"
                )}
              </button>
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={RECAPTCHA_SITE_KEY}
              />
            </form>
          </div>

          {/* Sağ panel - Görsel */}
          <div className="relative hidden lg:block">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(90deg, rgba(16,185,129,0.12) 0 40px, transparent 40px 80px)",
              }}
            />
            <div className="relative h-full flex items-center justify-center p-8">
              <img
                src="https://www.shutterstock.com/shutterstock/photos/1395298487/display_1500/stock-photo-focused-woman-wearing-headphones-using-laptop-in-cafe-writing-notes-attractive-female-student-1395298487.jpg"
                alt="Online learning"
                className="h-[80%] w-[78%] object-cover rounded-3xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 z-[1000] bg-black/10 backdrop-blur-[1px] grid place-items-center">
          <div className="bg-white/90 rounded-2xl px-6 py-5 shadow-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-500 border-t-transparent" />
            <span className="text-sm text-gray-700">Giriş yapılıyor…</span>
          </div>
        </div>
      )}

      {/* ✅ InfoModal */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
    </div>
  );
};

export default LoginModal;
